/**
 * Admin "Sign-in Helper" — generate a one-time code for a user when the
 * platform mailbox is down or the user can't reach their inbox. The code
 * is shown on screen so the admin can read it to the user on a call. No
 * email is dispatched. The code behaves exactly like a normal OTP (same
 * TTL, single-use, invalidates prior codes).
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, KeyRound, Mail, CheckCircle2, AlertTriangle, Copy } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function SignInHelper() {
    const [email, setEmail] = useState("");
    const [busy, setBusy] = useState(false);
    const [issued, setIssued] = useState(null);
    const [status, setStatus] = useState(null);

    useEffect(() => {
        api.get("/email/status").then(({ data }) => setStatus(data)).catch(() => {});
    }, []);

    async function issue(e) {
        e.preventDefault();
        if (!email) return;
        setBusy(true);
        setIssued(null);
        try {
            const { data } = await api.post("/auth/otp/admin/issue", { email: email.trim().toLowerCase() });
            setIssued(data);
            toast.success("Sign-in code generated");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    }

    function copyCode() {
        if (!issued?.code) return;
        navigator.clipboard.writeText(issued.code);
        toast.success("Code copied");
    }

    const emailDown = status && !status.outlook?.connected && !status.platform_fallback?.connected;

    return (
        <div className="max-w-2xl">
            <div className="mb-6">
                <div className="text-xs uppercase tracking-wider text-[#5E636E]">Admin tools</div>
                <h1 className="mt-1 text-2xl font-semibold text-[#0F1115]" data-testid="sign-in-helper-title">
                    Sign-in Helper
                </h1>
                <p className="mt-1 text-sm text-[#5E636E]">
                    Generate a one-time sign-in code for any active user in your firm.
                    Use this when a client can't receive the emailed code — read the
                    code to them over phone or chat.
                </p>
            </div>

            {status && (
                <div className="mb-4 rounded-lg border border-[#E4E4E7] bg-white p-3 text-sm">
                    <div className="flex items-center gap-2 font-medium text-[#0F1115]">
                        <Mail className="h-4 w-4" />
                        Email delivery status
                    </div>
                    <div className="mt-2 space-y-1 text-[#5E636E]">
                        <div className="flex items-center gap-2">
                            {status.automated_emails_disabled ? (
                                <AlertTriangle className="h-3.5 w-3.5 text-amber-600" />
                            ) : (
                                <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
                            )}
                            <span>
                                Automated emails:{" "}
                                <b className={status.automated_emails_disabled ? "text-amber-700" : "text-emerald-700"}>
                                    {status.automated_emails_disabled ? "OFF (all scheduler nudges paused)" : "ON"}
                                </b>
                            </span>
                        </div>
                        <div className="flex items-center gap-2">
                            {status.outlook?.connected || status.platform_fallback?.connected ? (
                                <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
                            ) : (
                                <AlertTriangle className="h-3.5 w-3.5 text-red-600" />
                            )}
                            <span>
                                Outlook mailbox:{" "}
                                <b>{status.outlook?.connected ? "connected" : (status.platform_fallback?.connected ? "fallback only" : "not reachable")}</b>
                                {status.outlook?.mailbox ? ` (${status.outlook.mailbox})` : ""}
                            </span>
                        </div>
                    </div>
                    {emailDown && (
                        <div className="mt-3 rounded-md border border-red-200 bg-red-50 p-2 text-xs text-red-800">
                            <b>Outbound email is currently down.</b> The Microsoft Graph app secret has likely expired.
                            Rotate it in Azure → App registrations → Certificates &amp; secrets, then update{" "}
                            <code>MS_GRAPH_CLIENT_SECRET</code> in backend .env. Use this helper to log users in meanwhile.
                        </div>
                    )}
                </div>
            )}

            <form onSubmit={issue} className="co-card co-card-pad space-y-4">
                <div>
                    <Label htmlFor="sih-email">User email</Label>
                    <Input
                        id="sih-email"
                        type="email"
                        placeholder="client@example.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="mt-1"
                        data-testid="sign-in-helper-email"
                        autoComplete="off"
                    />
                    <p className="mt-1 text-xs text-[#5E636E]">
                        Must be an active user in your firm. Issuing a new code
                        invalidates any previous unused codes for this email.
                    </p>
                </div>
                <Button
                    type="submit"
                    disabled={busy || !email}
                    data-testid="sign-in-helper-generate"
                    className="w-full"
                >
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />}
                    Generate sign-in code
                </Button>
            </form>

            {issued && (
                <div className="mt-6 rounded-lg border-2 border-emerald-300 bg-emerald-50 p-5" data-testid="sign-in-helper-result">
                    <div className="text-xs font-medium uppercase tracking-wider text-emerald-800">
                        One-time code for {issued.email}
                    </div>
                    <div className="mt-3 flex items-center gap-3">
                        <code
                            className="rounded-md border border-emerald-300 bg-white px-4 py-3 font-mono text-3xl font-bold tracking-[0.4em] text-[#0F1115]"
                            data-testid="sign-in-helper-code"
                        >
                            {issued.code}
                        </code>
                        <Button type="button" variant="outline" onClick={copyCode} data-testid="sign-in-helper-copy">
                            <Copy className="h-4 w-4" />
                            Copy
                        </Button>
                    </div>
                    <div className="mt-3 text-xs text-emerald-900">
                        Expires in {issued.ttl_minutes} minutes. Give the code to{" "}
                        <b>{issued.user_name || issued.email}</b> and ask them to enter it on the login screen.
                    </div>
                </div>
            )}
        </div>
    );
}
